import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, from, of, switchMap, withLatestFrom } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MeasureActions } from '../measure.actions';
import { ChoiceService } from '../../services/choice.service';
import { Store } from '@ngrx/store';
import { selectCurrentSurveyVersionId } from '../measure.selector';
import { defaultChoiceDisplayConfig } from '../../config/default-choice.config';

@Injectable()
export class ChoicesEffects {
  currentMeasureVersionId = this.store.select(selectCurrentSurveyVersionId);

  addChoice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.addChoice),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.choiceService
          .addChoice(action.questionId, defaultChoiceDisplayConfig, action.validationTypeOid, action.isTextEntry)
          .pipe(
            map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
            catchError(() => of(MeasureActions.addChoiceFailure())),
          ),
      ),
    ),
  );
  addChoiceLinkert$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.addChoiceLinkert),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.choiceService
          .addChoiceLinkert(
            action.questionId,
            defaultChoiceDisplayConfig,
            action.validationTypeOid,
            action.isTextEntry,
            action.name,
          )
          .pipe(
            map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
            catchError(() => of(MeasureActions.addChoiceFailure())),
          ),
      ),
    ),
  );

  updateChoice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateChoiceDisplay),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.choiceService.updateChoiceDisplay(action.choiceId, action.display).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.updateChoiceDisplayFailure())),
        ),
      ),
    ),
  );

  updateIsTextEntry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateIsTextEntry),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.choiceService.updateIsTextEntry(action.choiceId, action.isTextEntry, action.validationType).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.updateIsTextEntryFailure())),
        ),
      ),
    ),
  );

  updateChoiceValue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateChoiceValue),
      withLatestFrom(this.store.select(selectCurrentSurveyVersionId)),
      filter(([action, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.choiceService.updateChoiceValue(action.choiceId, action.value).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.updateChoiceValueFailure())),
        ),
      ),
    ),
  );

  updateChoiceValidationType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateChoiceValidationType),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.choiceService.updateChoiceValidationType(action.choiceId, action.validationType).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.updateChoiceValidationTypeFailure())),
        ),
      ),
    ),
  );

  updateChoiceHideInOpenEndedToggle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateChoiceHideInOpenEndedToggle),
      withLatestFrom(this.store.select(selectCurrentSurveyVersionId)),
      filter(([_, surveyVersionId]) => surveyVersionId != null),
      switchMap(([{ choiceId, hideInOpenEndedToggle }, surveyVersionId]) =>
        this.choiceService.updateChoiceHideInOpenEndedToggle(choiceId, hideInOpenEndedToggle).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError((err) => of(MeasureActions.updateChoiceHideInOpenEndedToggleFailure({ error: err.message }))),
        ),
      ),
    ),
  );

  updateChoiceUseCustomScaleValues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateChoiceUseCustomScaleValues),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId != null),
      switchMap(([{ choiceId, useCustomScaleValues }, surveyVersionId]) =>
        this.choiceService.updateChoiceUseCustomScaleValues(choiceId, useCustomScaleValues).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId: surveyVersionId as number })),
          catchError((err) =>
            of(
              MeasureActions.updateChoiceUseCustomScaleValuesFailure({
                error: err?.message ?? 'updateChoiceUseCustomScaleValues failed',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  // The question component reorders its choices optimistically; a failed save reloads the tree so the
  // list snaps back to the persisted order.
  reorderChoice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.reorderChoice),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId != null),
      switchMap(([{ choiceId, index }, surveyVersionId]) =>
        this.choiceService.updateChoiceIndex(choiceId, index).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId: surveyVersionId as number })),
          catchError(() =>
            from([
              MeasureActions.reorderChoiceFailure(),
              MeasureActions.getSurveyVersionTree({ surveyVersionId: surveyVersionId as number }),
            ]),
          ),
        ),
      ),
    ),
  );

  removeChoice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.removeChoice),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.choiceService.removeChoice(action.choiceId).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.removeChoiceFailure())),
        ),
      ),
    ),
  );

  updateChoiceTextEntryDefault$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateChoiceTextEntryDefault),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, versionId]) => versionId != null),
      switchMap(([{ choiceId, textEntryDefault }, versionId]) =>
        this.choiceService.updateChoiceTextEntryDefault(choiceId, textEntryDefault).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId: versionId! })),
          catchError((err) =>
            of(
              MeasureActions.updateChoiceTextEntryDefaultFailure({
                error: err?.message ?? 'updateChoiceTextEntryDefault failed',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  updateChoiceDataExportTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateChoiceDataExportTag),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId != null),
      switchMap(([{ choiceId, dataExportTag }, surveyVersionId]) =>
        this.choiceService.updateChoiceDataExportTag(choiceId, dataExportTag).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId: surveyVersionId as number })),
          catchError((err) =>
            of(
              MeasureActions.updateChoiceDataExportTagFailure({
                error: err?.message ?? 'updateChoiceDataExportTag failed',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  constructor(
    private store: Store,
    private actions$: Actions,
    private choiceService: ChoiceService,
  ) {}
}
